// Centralized BILINGUAL content for XV Capital Partners — strategic advisory cabinet.
// Tone : confidentiel, rare, sobre, international. Aucune formulation pouvant
// suggérer gestion de fonds, placement, conseil en investissement réglementé,
// intermédiation ou promesse implicite de financement.
//
// Public pages consume this via `useSiteContent()` which returns the FR or EN
// version based on the current language from `useI18n()`.

import { useI18n } from "@/lib/i18n";

// ─── POSITIONING ─────────────────────────────────────────────
const POSITIONING_FR = {
    tagline: "Strategic advisory · Côte d'Azur",
    promise:
        "Cabinet indépendant à la croisée du développement d'entreprise, de la structuration patrimoniale et des réseaux internationaux d'experts.",
};
const POSITIONING_EN = {
    tagline: "Strategic advisory · French Riviera",
    promise:
        "Independent boutique at the intersection of corporate development, wealth structuring and international expert networks.",
};

// ─── THREE INSTRUMENTS ───────────────────────────────────────
const INSTRUMENTS_FR = [
    {
        kicker: "I",
        title: "Architecture financière & patrimoniale",
        subtitle: "Architecture advisory",
        ticket: "Mandats sur-mesure",
        desc: "Conception, à la mesure de chaque dirigeant et family office, d'une architecture financière et patrimoniale cohérente — coordonnée, discrète, calibrée pour le long terme. À la croisée des intérêts privés et du développement économique.",
        points: [
            "Cadrage stratégique et patrimonial complet",
            "Coordination des dimensions juridique, fiscale et financière",
            "Préparation institutionnelle des dossiers sensibles",
            "Vision long terme, ancrage territorial",
        ],
    },
    {
        kicker: "II",
        title: "Opérations de croissance & de transmission",
        subtitle: "Operations advisory",
        ticket: "Approche sélective",
        desc: "Accompagnement stratégique des opérations de développement, de croissance et de transmission — coordination d'experts indépendants, préservation des marges de manœuvre du dirigeant et accès à un réseau international de partenaires.",
        points: [
            "Accompagnement des opérations de développement",
            "Coordination des dimensions humaines et financières",
            "Préservation de la gouvernance et de la vision",
            "Accès à un réseau international de partenaires",
        ],
    },
    {
        kicker: "III",
        title: "Ingénierie de financement & de structuration",
        subtitle: "Financing engineering",
        ticket: "Solutions adaptées",
        desc: "Solutions de financement adaptées aux enjeux patrimoniaux et entrepreneuriaux — coordination d'experts indépendants, calibrage des instruments, sécurisation des conditions d'exécution.",
        points: [
            "Coordination juridique, fiscale et financière",
            "Solutions calibrées aux enjeux patrimoniaux",
            "Sécurisation des conditions d'exécution",
            "Confidentialité absolue à chaque étape",
        ],
    },
];
const INSTRUMENTS_EN = [
    {
        kicker: "I",
        title: "Financial & wealth architecture",
        subtitle: "Architecture advisory",
        ticket: "Bespoke mandates",
        desc: "Design, tailored to each executive and family office, of a coherent financial and wealth architecture — coordinated, discreet, calibrated for the long term. At the intersection of private interests and economic development.",
        points: [
            "Comprehensive strategic and wealth scoping",
            "Coordination of legal, tax and financial dimensions",
            "Institutional preparation of sensitive files",
            "Long-term vision, territorial anchoring",
        ],
    },
    {
        kicker: "II",
        title: "Growth & transmission operations",
        subtitle: "Operations advisory",
        ticket: "Selective approach",
        desc: "Strategic support across development, growth and transmission operations — coordination of independent experts, preservation of the executive's room for manoeuvre and access to an international partner network.",
        points: [
            "Support across development operations",
            "Coordination of human and financial dimensions",
            "Preservation of governance and vision",
            "Access to an international partner network",
        ],
    },
    {
        kicker: "III",
        title: "Financing & structuring engineering",
        subtitle: "Financing engineering",
        ticket: "Tailored solutions",
        desc: "Financing solutions calibrated to wealth and entrepreneurial stakes — coordination of independent experts, instrument sizing, securing of execution conditions.",
        points: [
            "Legal, tax and financial coordination",
            "Solutions sized to wealth considerations",
            "Secured execution conditions",
            "Absolute confidentiality at every step",
        ],
    },
];

// ─── FOUR STAGES ─────────────────────────────────────────────
const STAGES_FR = [
    {
        n: "01",
        title: "Cadrage stratégique",
        objective:
            "Comprendre, en profondeur, la trajectoire du dirigeant, les arbitrages essentiels et les enjeux patrimoniaux.",
        points: [
            "Lecture stratégique et patrimoniale du dossier",
            "Définition des objectifs et des contraintes",
            "Identification des arbitrages structurants",
            "Cadrage de la méthode et du calendrier",
        ],
    },
    {
        n: "02",
        title: "Architecture sur-mesure",
        objective:
            "Coordonner la conception de l'architecture financière, juridique et fiscale avec les experts compétents.",
        points: [
            "Coordination des dimensions juridique et fiscale",
            "Calibrage de la structuration patrimoniale",
            "Mise en forme institutionnelle du dossier",
            "Anticipation des points sensibles",
        ],
    },
    {
        n: "03",
        title: "Coordination du réseau",
        objective:
            "Mobiliser, avec discrétion, un cercle restreint de partenaires et d'experts indépendants pertinents.",
        points: [
            "Coordination d'experts indépendants",
            "Accès à un réseau international de partenaires",
            "Dialogue sélectif et confidentiel",
            "Sécurisation des échanges sensibles",
        ],
    },
    {
        n: "04",
        title: "Exécution & accompagnement long terme",
        objective:
            "Accompagner la mise en œuvre, puis la trajectoire dans la durée — au-delà de l'opération elle-même.",
        points: [
            "Pilotage de l'exécution jusqu'au terme",
            "Coordination des conseils techniques",
            "Accompagnement post-opération",
            "Relation de confiance dans la durée",
        ],
    },
];
const STAGES_EN = [
    {
        n: "01",
        title: "Strategic scoping",
        objective:
            "Deeply understand the executive's trajectory, the essential trade-offs and the wealth considerations.",
        points: [
            "Strategic and wealth reading of the file",
            "Definition of objectives and constraints",
            "Identification of structuring trade-offs",
            "Framing of method and timeline",
        ],
    },
    {
        n: "02",
        title: "Bespoke architecture",
        objective:
            "Coordinate the design of the financial, legal and tax architecture with the relevant experts.",
        points: [
            "Coordination of legal and tax dimensions",
            "Sizing of wealth structuring",
            "Institutional formatting of the file",
            "Anticipation of sensitive points",
        ],
    },
    {
        n: "03",
        title: "Network coordination",
        objective:
            "Mobilise, with discretion, a restricted circle of relevant independent partners and experts.",
        points: [
            "Coordination of independent experts",
            "Access to an international partner network",
            "Selective and confidential dialogue",
            "Secured sensitive exchanges",
        ],
    },
    {
        n: "04",
        title: "Execution & long-term support",
        objective:
            "Support implementation, then the trajectory over time — beyond the operation itself.",
        points: [
            "Steering execution through to completion",
            "Coordination of technical advisors",
            "Post-operation support",
            "Trusted relationship over time",
        ],
    },
];

// ─── AUDIENCES ───────────────────────────────────────────────
const AUDIENCES_FR = [
    {
        kicker: "I",
        title: "Dirigeants & entrepreneurs",
        desc: "Dirigeants engagés dans des opérations de croissance, de transmission ou de structuration patrimoniale — privilégiant la discrétion, la sélectivité et la vision long terme.",
    },
    {
        kicker: "II",
        title: "Family offices",
        desc: "Family offices européens et internationaux recherchant un partenaire de confiance pour la coordination d'opérations patrimoniales sensibles et la mise en relation avec un réseau d'experts.",
    },
    {
        kicker: "III",
        title: "Groupes & ETI internationales",
        desc: "Groupes en expansion souhaitant rationaliser leur architecture financière et patrimoniale, ou conduire des opérations à dimension transfrontalière.",
    },
    {
        kicker: "IV",
        title: "Acteurs institutionnels & territoriaux",
        desc: "Collectivités, opérateurs publics et acteurs du développement économique cherchant à structurer des opérations d'envergure avec un accompagnement coordonné.",
    },
];
const AUDIENCES_EN = [
    {
        kicker: "I",
        title: "Executives & entrepreneurs",
        desc: "Executives engaged in growth, transmission or wealth structuring operations — favouring discretion, selectivity and long-term vision.",
    },
    {
        kicker: "II",
        title: "Family offices",
        desc: "European and international family offices seeking a trusted partner for the coordination of sensitive wealth operations and introductions to a network of experts.",
    },
    {
        kicker: "III",
        title: "International groups & mid-caps",
        desc: "Expanding groups wishing to rationalise their financial and wealth architecture, or carry out cross-border operations.",
    },
    {
        kicker: "IV",
        title: "Institutional & territorial actors",
        desc: "Local authorities, public operators and economic development actors seeking to structure large-scale operations with coordinated support.",
    },
];

// ─── COLLECTIVITES ───────────────────────────────────────────
const COLLECTIVITES_FR = {
    headline:
        "Accompagner les territoires dans la structuration de leurs grandes opérations.",
    intro:
        "XV Capital Partners coordonne, pour les acteurs publics et institutionnels, un écosystème international d'experts et de partenaires financiers de long terme — dédié à la structuration d'opérations d'envergure au service du développement territorial français.",
    fundFacts: [
        { value: "Long terme", suffix: "", label: "Horizon d'engagement" },
        { value: "Sélectif", suffix: "", label: "Cercle de partenaires" },
        { value: "International", suffix: "", label: "Ancrage du réseau" },
        { value: "Discret", suffix: "", label: "Mode opératoire" },
    ],
    program: {
        title: "Une approche dédiée aux territoires",
        amount: "Sur-mesure",
        amountLabel: "Architecture des opérations",
        eligibles: [
            "Infrastructures publiques structurantes",
            "Équipements de santé et d'éducation",
            "Projets à impact territorial durable",
            "Refonte d'architectures financières existantes",
        ],
    },
    conditions: [
        { k: "Approche", v: "Mandats sélectifs, mode opératoire confidentiel" },
        { k: "Horizon", v: "Long terme, ancrage durable" },
        { k: "Périmètre", v: "Opérations d'envergure, projets structurants" },
        { k: "Coordination", v: "Experts indépendants & partenaires internationaux" },
        { k: "Calendrier", v: "Phase de cadrage de plusieurs mois" },
    ],
    projects: ["Port de Marseille", "Paris", "Bagnolet", "Mayotte", "Caraïbes françaises"],
    rationale: [
        "Stabilité économique et institutionnelle de la France",
        "Volonté de soutenir des projets à impact durable et sociétal",
        "Qualité des signatures publiques françaises",
    ],
};
const COLLECTIVITES_EN = {
    headline:
        "Supporting territories in the structuring of their major operations.",
    intro:
        "For public and institutional actors, XV Capital Partners coordinates an international ecosystem of experts and long-term financial partners — dedicated to the structuring of large-scale operations serving French territorial development.",
    fundFacts: [
        { value: "Long term", suffix: "", label: "Commitment horizon" },
        { value: "Selective", suffix: "", label: "Partner circle" },
        { value: "International", suffix: "", label: "Network reach" },
        { value: "Discreet", suffix: "", label: "Operating mode" },
    ],
    program: {
        title: "A dedicated approach for territories",
        amount: "Bespoke",
        amountLabel: "Operation architecture",
        eligibles: [
            "Structuring public infrastructure",
            "Health and education facilities",
            "Projects with lasting territorial impact",
            "Redesign of existing financial architectures",
        ],
    },
    conditions: [
        { k: "Approach", v: "Selective mandates, confidential operating mode" },
        { k: "Horizon", v: "Long term, lasting anchoring" },
        { k: "Scope", v: "Large-scale operations, structuring projects" },
        { k: "Coordination", v: "Independent experts & international partners" },
        { k: "Timeline", v: "Multi-month scoping phase" },
    ],
    projects: ["Port of Marseille", "Paris", "Bagnolet", "Mayotte", "French Caribbean"],
    rationale: [
        "Economic and institutional stability of France",
        "Commitment to supporting lasting and societal-impact projects",
        "Quality of French public signatures",
    ],
};

// ─── FAQ ─────────────────────────────────────────────────────
const FAQ_FR = [
    {
        q: "Quel est votre positionnement ?",
        a: "XV Capital Partners est un cabinet indépendant de strategic advisory, à la croisée du développement d'entreprise, de la structuration patrimoniale, des opérations complexes et des réseaux internationaux de partenaires et d'experts. Nous n'exerçons aucune activité de gestion, de placement ou d'intermédiation financière régulée.",
    },
    {
        q: "Quelle est votre approche ?",
        a: "Une approche sur-mesure et haut de gamme, fondée sur la sélectivité, la discrétion et la vision long terme. Nous coordonnons, à chaque mandat, un cercle restreint d'experts indépendants et de partenaires internationaux — calibré à la nature exacte de l'opération.",
    },
    {
        q: "Comment se déroule un mandat ?",
        a: "Après un premier entretien d'évaluation, une proposition de mission précise le périmètre, les livrables, le calendrier et les honoraires de cadrage. La mobilisation des experts et partenaires intervient dans le cadre du mandat convenu.",
    },
    {
        q: "Quels types de clients accompagnez-vous ?",
        a: "Dirigeants, entrepreneurs, family offices, groupes en expansion et acteurs institutionnels souhaitant être accompagnés sur des opérations complexes, sensibles ou transfrontalières — toujours dans un cadre confidentiel.",
    },
    {
        q: "En quoi êtes-vous différent d'un acteur généraliste ?",
        a: "Nous ne sommes ni un cabinet de masse, ni un acteur régulé de la finance. Nous sommes un cabinet sélectif d'architecture financière et patrimoniale, qui privilégie le sur-mesure, la confidentialité et l'engagement personnel du fondateur sur chaque dossier accepté.",
    },
    {
        q: "Quels sont vos délais d'intervention ?",
        a: "Le calendrier dépend de la complexité du dossier et de sa maturité. Les priorités, les étapes et les échéances sont définies dès le cadrage initial.",
    },
    {
        q: "Comment garantissez-vous la confidentialité ?",
        a: "Les informations sont partagées avec les seuls intervenants nécessaires à la mission, dans un cadre de confidentialité convenu avec le client. La confiance se construit par la qualité de l'accompagnement et la discrétion sur les dossiers confiés.",
    },
    {
        q: "Quel est votre périmètre géographique ?",
        a: "Notre ancrage est national ; notre cercle est international. Le cabinet intervient depuis la Côte d'Azur et coordonne un réseau de partenaires en Europe, en Suisse, au Royaume-Uni, au Moyen-Orient, en Asie et en Amérique.",
    },
];
const FAQ_EN = [
    {
        q: "What is your positioning?",
        a: "XV Capital Partners is an independent strategic advisory boutique at the intersection of corporate development, wealth structuring, complex operations and international networks of partners and experts. We carry out no regulated management, placement or financial intermediation activity.",
    },
    {
        q: "What is your approach?",
        a: "A bespoke, high-end approach grounded in selectivity, discretion and long-term vision. For each mandate, we coordinate a restricted circle of independent experts and international partners — calibrated to the exact nature of the operation.",
    },
    {
        q: "How does a mandate unfold?",
        a: "Following an initial evaluation meeting, a mission proposal sets out the scope, deliverables, timeline and scoping fees. Experts and partners are mobilised within the agreed mandate.",
    },
    {
        q: "Which types of clients do you support?",
        a: "Executives, entrepreneurs, family offices, expanding groups and institutional actors seeking support on complex, sensitive or cross-border operations — always within a confidential framework.",
    },
    {
        q: "How do you differ from a generalist firm?",
        a: "We are neither a mass-market firm nor a regulated financial actor. We are a selective boutique for financial and wealth architecture, favouring bespoke work, confidentiality and the personal commitment of the founder on every accepted file.",
    },
    {
        q: "What are your intervention timelines?",
        a: "The timeline depends on the complexity and maturity of the file. Priorities, stages and deadlines are defined from the initial scoping onwards.",
    },
    {
        q: "How do you guarantee confidentiality?",
        a: "Information is shared only with the stakeholders required for the mission, within a confidentiality framework agreed with the client. Trust is built through the quality of the support and discretion over the files entrusted to us.",
    },
    {
        q: "What is your geographical scope?",
        a: "Our anchoring is national; our circle is international. The firm operates from the French Riviera and coordinates a network of partners across Europe, Switzerland, the United Kingdom, the Middle East, Asia and the Americas.",
    },
];

// ─── OPERATIONAL PARTNERS ────────────────────────────────────
const OPERATIONAL_PARTNERS_FR = [
    "Cabinets d'avocats d'affaires",
    "Fiscalistes patrimoniaux & corporate",
    "Experts-comptables & auditeurs",
    "Banques privées européennes",
    "Family offices internationaux",
    "Partenaires financiers de long terme",
    "Spécialistes due diligence",
    "Notaires & études internationales",
    "Conseils en gouvernance",
    "Experts sectoriels indépendants",
];
const OPERATIONAL_PARTNERS_EN = [
    "Corporate law firms",
    "Private and corporate tax specialists",
    "Chartered accountants & auditors",
    "European private banks",
    "International family offices",
    "Long-term financial partners",
    "Due diligence specialists",
    "Notaries & international practices",
    "Governance advisors",
    "Independent sector experts",
];

// ─── FOUNDER ─────────────────────────────────────────────────
const FOUNDER_FR = {
    name: "Joel Kotho",
    role: "Fondateur & Managing Partner",
    domain:
        "Cabinet fondé par un ancien sportif de haut niveau devenu entrepreneur — une approche fondée sur l'engagement, la discrétion et la vision long terme.",
    institutions: [],
    pillars: [
        {
            t: "Engagement",
            d: "Présence personnelle du fondateur sur chaque mandat accepté — pas de délégation, pas de standardisation.",
        },
        {
            t: "Discipline",
            d: "La culture de la performance, transposée à la conduite d'opérations sensibles : rigueur, anticipation, sang-froid.",
        },
        {
            t: "Résilience",
            d: "Les opérations exigeantes traversent des temps difficiles. La capacité à tenir la trajectoire compte plus que tout.",
        },
        {
            t: "Confiance",
            d: "Une relation construite dans la durée, dans le calme, hors des projecteurs. La confiance se construit par la qualité de l'accompagnement et la discrétion sur les dossiers confiés.",
        },
    ],
};
const FOUNDER_EN = {
    name: "Joel Kotho",
    role: "Founder & Managing Partner",
    domain:
        "A boutique founded by a former high-level athlete turned entrepreneur — an approach grounded in commitment, discretion and long-term vision.",
    institutions: [],
    pillars: [
        {
            t: "Commitment",
            d: "The founder's personal presence on every accepted mandate — no delegation, no standardisation.",
        },
        {
            t: "Discipline",
            d: "The culture of performance, applied to the conduct of sensitive operations: rigour, anticipation, composure.",
        },
        {
            t: "Resilience",
            d: "Demanding operations pass through difficult moments. The ability to hold the course matters more than anything.",
        },
        {
            t: "Trust",
            d: "A relationship built over time, in quiet, away from the spotlight. Trust is earned through the quality of the support and discretion over the files entrusted to us.",
        },
    ],
};

// ─── COMPLIANCE ──────────────────────────────────────────────
const COMPLIANCE_FR =
    "XV Capital Partners est un cabinet indépendant de strategic advisory et d'architecture financière. Le cabinet n'exerce aucune activité de gestion, de placement financier, de conseil en investissement réglementé, ni d'intermédiation financière. Il ne procède à aucune sollicitation publique d'épargne et n'exerce aucune activité de démarchage bancaire ou financier au sens des articles L. 341-1 et suivants du Code monétaire et financier.";
const COMPLIANCE_EN =
    "XV Capital Partners is an independent strategic advisory and financial architecture boutique. The firm carries out no asset management, no investment placement, no regulated investment advice and no financial intermediation. It engages in no public solicitation of savings and no banking or financial canvassing within the meaning of articles L. 341-1 et seq. of the French Monetary and Financial Code.";

// ─── BUNDLE ──────────────────────────────────────────────────
const BUNDLES = {
    fr: {
        POSITIONING: POSITIONING_FR,
        INSTRUMENTS: INSTRUMENTS_FR,
        STAGES: STAGES_FR,
        AUDIENCES: AUDIENCES_FR,
        COLLECTIVITES: COLLECTIVITES_FR,
        FAQ: FAQ_FR,
        OPERATIONAL_PARTNERS: OPERATIONAL_PARTNERS_FR,
        FOUNDER: FOUNDER_FR,
        COMPLIANCE_LINE: COMPLIANCE_FR,
    },
    en: {
        POSITIONING: POSITIONING_EN,
        INSTRUMENTS: INSTRUMENTS_EN,
        STAGES: STAGES_EN,
        AUDIENCES: AUDIENCES_EN,
        COLLECTIVITES: COLLECTIVITES_EN,
        FAQ: FAQ_EN,
        OPERATIONAL_PARTNERS: OPERATIONAL_PARTNERS_EN,
        FOUNDER: FOUNDER_EN,
        COMPLIANCE_LINE: COMPLIANCE_EN,
    },
};

export function useSiteContent() {
    const { lang } = useI18n();
    return BUNDLES[lang] || BUNDLES.fr;
}

// Legacy named exports (FR defaults — kept for admin/cockpit/client components
// that are not internationalised; public pages must use useSiteContent()).
export const POSITIONING = POSITIONING_FR;
export const INSTRUMENTS = INSTRUMENTS_FR;
export const STAGES = STAGES_FR;
export const AUDIENCES = AUDIENCES_FR;
export const COLLECTIVITES = COLLECTIVITES_FR;
export const FAQ = FAQ_FR;
export const OPERATIONAL_PARTNERS = OPERATIONAL_PARTNERS_FR;
export const FOUNDER = FOUNDER_FR;
export const COMPLIANCE_LINE = COMPLIANCE_FR;
